import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { processFormData } from '../utils/inputSanitizer'
import SquarePaymentCard from '../components/SquarePaymentCard'
import { prysmDB } from '../utils/database'
import clientLogger from '../utils/clientLogger'
import apiService from '../services/apiService'

// Session storage key for user data
const SESSION_KEY = 'registration_data'

const Registration = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    facebookUrl: '',
    relationshipStatus: '',
    gender: '',
    birthday: '',
    mobileNumber: '',
    zipCode: '',
    city: '',
    state: '',
    country: ''
  })

  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [consolidatedErrors, setConsolidatedErrors] = useState(null)
  const [showPayment, setShowPayment] = useState(false)
  const [isProcessingSquareCustomer, setIsProcessingSquareCustomer] = useState(false)
  const [squareCustomerError, setSquareCustomerError] = useState(null)
  const [validationStatus, setValidationStatus] = useState({
    username: { checking: false, available: null, message: '' },
    email: { checking: false, available: null, message: '' }
  })
  const [isRegistrationSubmitted, setIsRegistrationSubmitted] = useState(false)

  // Check registration submission status on component load
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const registrationResponse = await prysmDB.get('registration_data', 'registration_response')
        if (registrationResponse && registrationResponse.status === 'success') {
          console.log('[Registration] Registration already submitted successfully')
          setIsRegistrationSubmitted(true)
        } else {
          console.log('[Registration] No successful registration found')
          setIsRegistrationSubmitted(false)
        }
      } catch (error) {
        console.error('[Registration] Error checking registration status:', error)
        setIsRegistrationSubmitted(false)
      }
    }

    checkRegistrationStatus()
  }, [])

  // Add effect to scroll to bottom when errors are present
  useEffect(() => {
    if (Object.keys(errors).length > 0 || consolidatedErrors) {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, [errors, consolidatedErrors])

  // Function to update navbar user info
  const updateNavbarUserInfo = (data) => {
    const userInfo = {
      name: `${data.first_name || data.firstName} ${data.last_name || data.lastName}`,
      email: data.email
    }
    // Dispatch a custom event that the Navbar component can listen to
    window.dispatchEvent(new CustomEvent('userInfoUpdate', { detail: userInfo }))
  }

  // API function to check username availability
  const checkUsernameAvailability = useCallback(async (username) => {
    if (!username || username.trim().length < 3) {
      return { available: null, message: '' }
    }

    // Prevent duplicate calls for the same username
    const trimmedUsername = username.trim()
    if (validationStatus.username.lastChecked === trimmedUsername) {
      return validationStatus.username
    }

    try {
      const response = await apiService.checkUsernameAvailability(username.trim())

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()

      // If responseData.data is empty, username is available
      const isAvailable = !responseData.data || responseData.data.length === 0

      const result = {
        available: isAvailable,
        message: isAvailable ? 'Username is available' : 'Username is already taken',
        lastChecked: trimmedUsername
      }

      // Update validation status with the result
      setValidationStatus(prev => ({
        ...prev,
        username: result
      }))

      return result
    } catch (error) {
      console.error('[Registration] Error checking username availability:', error)
      clientLogger.logError('username_check', `Username availability check failed: ${error.message}`, error, {
        action: 'check_username_availability',
        component: 'Registration',
        username: username
      })
      return { available: null, message: 'Unable to verify username availability' }
    }
  }, [])

  // API function to check email availability
  const checkEmailAvailability = useCallback(async (email) => {
    if (!email || !email.includes('@')) {
      return { available: null, message: '' }
    }

    // Prevent duplicate calls for the same email
    const trimmedEmail = email.trim().toLowerCase()
    if (validationStatus.email.lastChecked === trimmedEmail) {
      return validationStatus.email
    }

    try {
      const response = await apiService.checkEmailAvailability(email.trim().toLowerCase())

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()

      // If responseData.data is empty, email is available
      const isAvailable = !responseData.data || responseData.data.length === 0

      const result = {
        available: isAvailable,
        message: isAvailable ? 'Email is available' : 'Email is already registered',
        lastChecked: trimmedEmail
      }

      // Update validation status with the result
      setValidationStatus(prev => ({
        ...prev,
        email: result
      }))

      return result
    } catch (error) {
      console.error('[Registration] Error checking email availability:', error)
      clientLogger.logError('email_check', `Email availability check failed: ${error.message}`, error, {
        action: 'check_email_availability',
        component: 'Registration',
        email: email
      })
      return { available: null, message: 'Unable to verify email availability' }
    }
  }, [])

  // Debounced validation for username
  const debouncedUsernameValidation = useCallback((() => {
    let timeoutId
    return (username) => {
      clearTimeout(timeoutId)

      // Reset validation status
      setValidationStatus(prev => ({
        ...prev,
        username: { checking: true, available: null, message: 'Checking availability...' }
      }))

      timeoutId = setTimeout(async () => {
        const result = await checkUsernameAvailability(username)
        setValidationStatus(prev => ({
          ...prev,
          username: result
        }))
      }, 1000) // 1000ms delay (1 second)
    }
  })(), [checkUsernameAvailability])

  // Debounced validation for email
  const debouncedEmailValidation = useCallback((() => {
    let timeoutId
    return (email) => {
      clearTimeout(timeoutId)

      // Reset validation status
      setValidationStatus(prev => ({
        ...prev,
        email: { checking: true, available: null, message: 'Checking availability...' }
      }))

      timeoutId = setTimeout(async () => {
        const result = await checkEmailAvailability(email)
        setValidationStatus(prev => ({
          ...prev,
          email: result
        }))
      }, 1500) // 1500ms delay (1.5 seconds)
    }
  })(), [checkEmailAvailability])

  // Load form data from storage on component mount and when returning to page
  useEffect(() => {
    const loadFormData = async () => {
      try {
        const savedData = await prysmDB.get('registration_data', 'current_registration')
        if (savedData) {
          console.log('[Registration] Loaded from storage:', savedData)
          console.log('[Registration] Username in saved data:', savedData.username)

          // Create a new form data object with all fields
          const rawFormData = {
            firstName: savedData.first_name || '',
            lastName: savedData.last_name || '',
            email: savedData.email || '',
            username: savedData.username || '',
            password: savedData.password || '',
            confirmPassword: savedData.confirmPassword || '',
            facebookUrl: savedData.facebook_url || '',
            relationshipStatus: savedData.relationship_status || '',
            gender: savedData.gender || '',
            birthday: savedData.birthday || '',
            mobileNumber: savedData.mobile_number || '',
            zipCode: savedData.zip_code || '',
            city: savedData.city || '',
            state: savedData.state || '',
            country: savedData.country || ''
          }

          console.log('[Registration] Raw form data before sanitization:', rawFormData)
          console.log('[Registration] Username in raw form data:', rawFormData.username)

          // Sanitize the loaded data
          const { sanitizedData } = processFormData(rawFormData)
          console.log('[Registration] Setting sanitized form data:', sanitizedData)
          console.log('[Registration] Username in sanitized data:', sanitizedData.username)
          setFormData(sanitizedData)

          // Update navbar with the loaded data
          updateNavbarUserInfo(savedData)
        }
      } catch (error) {
        console.error('[Registration] Error loading storage data:', error)
      }
    }

    // Load data immediately
    loadFormData()

    // Listen for navigation events to reload data when returning to this page
    const handleNavigation = () => {
      loadFormData()
    }

    // Listen for focus events (when user returns to tab)
    window.addEventListener('focus', handleNavigation)

    // Listen for visibility changes (when user switches tabs)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        handleNavigation()
      }
    })

    // Listen for custom navigation events
    window.addEventListener('registrationDataUpdated', handleNavigation)

    // Cleanup listeners
    return () => {
      window.removeEventListener('focus', handleNavigation)
      document.removeEventListener('visibilitychange', handleNavigation)
      window.removeEventListener('registrationDataUpdated', handleNavigation)
    }
  }, []) // Keep empty deps since we handle updates via event listeners

  // Reload data when navigating to this page
  useEffect(() => {
    const reloadDataOnNavigation = async () => {
      if (location.pathname === '/register') {
        try {
          const savedData = await prysmDB.get('registration_data', 'current_registration')
          if (savedData) {
            console.log('[Registration] Reloading data from storage on navigation:', savedData)

            const rawFormData = {
              firstName: savedData.first_name || '',
              lastName: savedData.last_name || '',
              email: savedData.email || '',
              username: savedData.username || '',
              password: savedData.password || '',
              confirmPassword: savedData.confirmPassword || '',
              facebookUrl: savedData.facebook_url || '',
              relationshipStatus: savedData.relationship_status || '',
              gender: savedData.gender || '',
              birthday: savedData.birthday || '',
              mobileNumber: savedData.mobile_number || '',
              zipCode: savedData.zip_code || '',
              city: savedData.city || '',
              state: savedData.state || '',
              country: savedData.country || ''
            }

            const { sanitizedData } = processFormData(rawFormData)
            console.log('[Registration] Updating form data from storage:', sanitizedData)
            setFormData(sanitizedData)
            updateNavbarUserInfo(savedData)
          }
        } catch (error) {
          console.error('[Registration] Error reloading storage data:', error)
        }
      }
    }

    reloadDataOnNavigation()
  }, [location.pathname])

  const handleChange = (e) => {
    const { name, value } = e.target

    try {
      // Process and sanitize the input in real-time
      const tempFormData = { ...formData, [name]: value }
      const { sanitizedData, processingErrors } = processFormData(tempFormData)

      // Update form data
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: sanitizedData[name]
      }))

      // Clear any existing errors for this field
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors }
        delete newErrors[name]
        return newErrors
      })

      // Real-time password confirmation validation
      if (name === 'confirmPassword' && formData.password) {
        if (sanitizedData.confirmPassword && formData.password !== sanitizedData.confirmPassword) {
          setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }))
        }
      }
      if (name === 'password' && formData.confirmPassword) {
        if (sanitizedData.password && sanitizedData.password !== formData.confirmPassword) {
          setErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }))
        } else {
          setErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors.confirmPassword
            return newErrors
          })
        }
      }

      // Real-time age validation
      if (name === 'birthday' && sanitizedData.birthday) {
        const birthDate = new Date(sanitizedData.birthday)
        const today = new Date()
        const age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()

        const actualAge = (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) ? age - 1 : age

        if (actualAge < 18) {
          setErrors(prev => ({ ...prev, birthday: 'You must be at least 18 years old to register' }))
        } else {
          setErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors.birthday
            return newErrors
          })
        }
      }

      // Trigger uniqueness validation for username and email
      if (name === 'username' && sanitizedData.username) {
        debouncedUsernameValidation(sanitizedData.username)
      }
      if (name === 'email' && sanitizedData.email) {
        debouncedEmailValidation(sanitizedData.email)
      }

      // If there are processing errors for this specific field, show a warning but continue
      const fieldError = processingErrors.find(error => error.field === name)
      if (fieldError) {
        console.warn(`Field ${name} processing error:`, fieldError)
        // You could optionally show a toast notification here
      }

    } catch (error) {
      console.error(`Error processing field ${name}:`, error)
      // Fallback: just update the form data without processing
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleBlur = (e) => {
    const { name } = e.target
    setTouched(prev => ({
      ...prev,
      [name]: true
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    try {
      // Clear previous consolidated errors
      setConsolidatedErrors(null)

      // Check uniqueness validation before proceeding
      const uniquenessErrors = {}

      if (validationStatus.username.available === false) {
        uniquenessErrors.username = 'Username is already taken'
      }

      if (validationStatus.email.available === false) {
        uniquenessErrors.email = 'Email is already registered'
      }

      // Age validation for form submission
      if (formData.birthday) {
        const birthDate = new Date(formData.birthday)
        const today = new Date()
        const age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()

        const actualAge = (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) ? age - 1 : age

        if (actualAge < 18) {
          uniquenessErrors.birthday = 'You must be at least 18 years old to register'
        }
      }

      // If there are uniqueness or age errors, don't proceed
      if (Object.keys(uniquenessErrors).length > 0) {
        setErrors(prevErrors => ({
          ...prevErrors,
          ...uniquenessErrors
        }))
        return
      }

      // Process form data: sanitize and validate
      const {
        sanitizedData,
        validationErrors,
        processingErrors,
        consolidatedErrors,
        isValid
      } = processFormData(formData)

      console.log('Form processing results:', {
        originalData: formData,
        sanitizedData,
        validationErrors,
        processingErrors,
        consolidatedErrors,
        isValid
      })

      // Update form data with sanitized values
      setFormData(sanitizedData)
      setErrors(validationErrors)
      setConsolidatedErrors(consolidatedErrors)

      if (isValid) {
        // Store sanitized data in session storage
        const dataToStore = {
          first_name: sanitizedData.firstName,
          last_name: sanitizedData.lastName,
          email: sanitizedData.email,
          username: sanitizedData.username,
          password: sanitizedData.password,
          confirmPassword: sanitizedData.confirmPassword,
          facebook_url: sanitizedData.facebookUrl,
          relationship_status: sanitizedData.relationshipStatus,
          gender: sanitizedData.gender,
          birthday: sanitizedData.birthday,
          mobile_number: sanitizedData.mobileNumber,
          zip_code: sanitizedData.zipCode,
          city: sanitizedData.city,
          state: sanitizedData.state,
          country: sanitizedData.country,
          processed_at: new Date().toISOString(),
          sanitized: true
        }

        console.log('[Registration] Saving final sanitized data to storage:', dataToStore)
        console.log('[Registration] Username being saved:', dataToStore.username)

        // Save to storage and then process with Square customer endpoint
        const saveFinalData = async () => {
          try {
            await prysmDB.set('registration_data', 'current_registration', dataToStore)
            console.log('[Registration] Final data saved successfully to storage')

            // Update navbar with the final data
            updateNavbarUserInfo(dataToStore)

            // Set loading state for Square customer processing
            setIsProcessingSquareCustomer(true)
            setSquareCustomerError(null)

            // Submit registration data to Square customer process endpoint
            try {
              console.log('[Registration] Submitting registration data to Square customer process endpoint')
              const response = await apiService.processSquareCustomer(dataToStore)

              if (response.ok) {
                const result = await response.json()
                console.log('[Registration] Square customer process successful:', result)

                // Check if the response indicates success
                if (result.success || result.status === 'success') {
                  console.log('[Registration] Square customer processing completed successfully, showing payment component')

                  // Save registration response to storage
                  try {
                    await prysmDB.set('registration_data', 'registration_response', result)
                    console.log('[Registration] Registration response saved to storage:', result)
                  } catch (error) {
                    console.error('[Registration] Error saving registration response to storage:', error)
                  }

                  setShowPayment(true)
                } else {
                  console.error('[Registration] Square customer process returned unsuccessful status:', result)
                  setSquareCustomerError('Square customer processing failed. Please try again.')
                }
              } else {
                console.error('[Registration] Square customer process failed:', response.status, response.statusText)
                setSquareCustomerError(`Square customer processing failed (${response.status}). Please try again.`)
              }
            } catch (squareError) {
              console.error('[Registration] Error submitting to Square customer process:', squareError)
              setSquareCustomerError('Error processing with Square. Please try again.')
            } finally {
              setIsProcessingSquareCustomer(false)
            }
          } catch (error) {
            console.error('[Registration] Error saving final data to storage:', error)
            setIsProcessingSquareCustomer(false)
            setSquareCustomerError('Error saving registration data. Please try again.')
          }
        }

        saveFinalData()
      } else {
        console.log('Form validation/processing failed:', {
          validationErrors,
          processingErrors,
          consolidatedErrors
        })
        // Errors and consolidated errors are already set by the processing function
      }
    } catch (error) {
      console.error('Error processing form:', error)

      // Create a consolidated error for unexpected errors
      const unexpectedError = {
        summary: 'Unexpected error occurred during form processing',
        details: [{
          field: 'Form Processing',
          originalValue: '(entire form)',
          errorType: error.name || 'UnexpectedError',
          errorMessage: error.message || 'An unknown error occurred',
          possibleSolutions: [
            'Try refreshing the page and starting over',
            'Check your internet connection',
            'Ensure all fields are filled out correctly',
            'Try using a different browser if problems persist'
          ]
        }],
        generalAdvice: [
          'This appears to be a system error rather than an input problem',
          'Please try refreshing the page and starting the form again',
          'If the problem continues, please contact support'
        ]
      }

      setConsolidatedErrors(unexpectedError)
      setErrors({
        submit: 'An unexpected error occurred while processing the form. Please see the detailed error report below.'
      })
    }
  }

  // If payment component should be shown, render it
  if (showPayment) {
    return <SquarePaymentCard/>
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div
          className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="w-full max-w-3xl relative z-10">
        <div
          className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform transition-all duration-500 hover:scale-[1.02] border border-gray-700">
          <div className="text-center mb-8">
            <h1
              className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500 mb-4 animate-fade-in">
              Registration
            </h1>
            <div
              className="h-1 w-20 bg-gradient-to-r from-pink-500 to-purple-600 mx-auto rounded-full animate-glow"></div>
            <p className="mt-4 text-gray-400">
              Please fill out the form below to register
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                  placeholder="Enter your first name"
                />
                {touched.firstName && errors.firstName && (
                  <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                  placeholder="Enter your last name"
                />
                {touched.lastName && errors.lastName && (
                  <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    className={`mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:outline-none focus:ring-2 transition-all duration-200 pr-10 ${validationStatus.email.checking
                      ? 'focus:border-yellow-500 focus:ring-yellow-500'
                      : validationStatus.email.available === true
                        ? 'focus:border-green-500 focus:ring-green-500 border-green-600'
                        : validationStatus.email.available === false
                          ? 'focus:border-red-500 focus:ring-red-500 border-red-600'
                          : 'focus:border-pink-500 focus:ring-pink-500'
                    }`}
                    placeholder="Enter your email"
                  />
                  {validationStatus.email.checking && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
                    </div>
                  )}
                  {validationStatus.email.available === true && !validationStatus.email.checking && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                  )}
                  {validationStatus.email.available === false && !validationStatus.email.checking && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </div>
                  )}
                </div>
                {touched.email && errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                )}
                {validationStatus.email.message && (
                  <p className={`mt-1 text-sm ${validationStatus.email.available === true ? 'text-green-400' :
                    validationStatus.email.available === false ? 'text-red-400' :
                      'text-yellow-400'
                  }`}>
                    {validationStatus.email.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
                <div className="relative">
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    className={`mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:outline-none focus:ring-2 transition-all duration-200 pr-10 ${validationStatus.username.checking
                      ? 'focus:border-yellow-500 focus:ring-yellow-500'
                      : validationStatus.username.available === true
                        ? 'focus:border-green-500 focus:ring-green-500 border-green-600'
                        : validationStatus.username.available === false
                          ? 'focus:border-red-500 focus:ring-red-500 border-red-600'
                          : 'focus:border-pink-500 focus:ring-pink-500'
                    }`}
                    placeholder="Choose a username"
                  />
                  {validationStatus.username.checking && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500"></div>
                    </div>
                  )}
                  {validationStatus.username.available === true && !validationStatus.username.checking && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                  )}
                  {validationStatus.username.available === false && !validationStatus.username.checking && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </div>
                  )}
                </div>
                {touched.username && errors.username && (
                  <p className="mt-1 text-sm text-red-400">{errors.username}</p>
                )}
                {validationStatus.username.message && (
                  <p className={`mt-1 text-sm ${validationStatus.username.available === true ? 'text-green-400' :
                    validationStatus.username.available === false ? 'text-red-400' :
                      'text-yellow-400'
                  }`}>
                    {validationStatus.username.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                  placeholder="Enter your password"
                />
                {touched.password && errors.password && (
                  <p className="mt-1 text-sm text-red-400">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                  placeholder="Confirm your password"
                />
                {touched.confirmPassword && errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Birthday</label>
                <input
                  type="date"
                  name="birthday"
                  value={formData.birthday}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                  max={new Date().toISOString().split('T')[0]}
                />
                {touched.birthday && errors.birthday && (
                  <p className="mt-1 text-sm text-red-400">{errors.birthday}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Facebook Profile URL</label>
                <input
                  type="text"
                  name="facebookUrl"
                  value={formData.facebookUrl}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                  placeholder="https://www.facebook.com/yourusername"
                />
                {touched.facebookUrl && errors.facebookUrl && (
                  <p className="mt-1 text-sm text-red-400">{errors.facebookUrl}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Mobile Number</label>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  pattern="[0-9]{10}"
                  inputMode="numeric"
                  maxLength="10"
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                  placeholder="1234567890"
                />
                {touched.mobileNumber && errors.mobileNumber && (
                  <p className="mt-1 text-sm text-red-400">{errors.mobileNumber}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Country</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                >
                  <option value="">Select Country</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="MX">Mexico</option>
                </select>
                {touched.country && errors.country && (
                  <p className="mt-1 text-sm text-red-400">{errors.country}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                  placeholder="Enter your city"
                />
                {touched.city && errors.city && (
                  <p className="mt-1 text-sm text-red-400">{errors.city}</p>
                )}
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-300 mb-1">{formData.country === 'CA' ? 'Province' : 'State'}</label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                >
                  <option value="">{formData.country === 'CA' ? 'Select Province' : 'Select State'}</option>
                  {formData.country === 'CA' ? (
                    // Canadian Provinces
                    <>
                      <option value="AB">Alberta</option>
                      <option value="BC">British Columbia</option>
                      <option value="MB">Manitoba</option>
                      <option value="NB">New Brunswick</option>
                      <option value="NL">Newfoundland and Labrador</option>
                      <option value="NS">Nova Scotia</option>
                      <option value="ON">Ontario</option>
                      <option value="PE">Prince Edward Island</option>
                      <option value="QC">Quebec</option>
                      <option value="SK">Saskatchewan</option>
                      <option value="NT">Northwest Territories</option>
                      <option value="NU">Nunavut</option>
                      <option value="YT">Yukon</option>
                    </>
                  ) : (
                    // US States
                    <>
                      <option value="AL">Alabama</option>
                      <option value="AK">Alaska</option>
                      <option value="AZ">Arizona</option>
                      <option value="AR">Arkansas</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="DE">Delaware</option>
                      <option value="FL">Florida</option>
                      <option value="GA">Georgia</option>
                      <option value="HI">Hawaii</option>
                      <option value="ID">Idaho</option>
                      <option value="IL">Illinois</option>
                      <option value="IN">Indiana</option>
                      <option value="IA">Iowa</option>
                      <option value="KS">Kansas</option>
                      <option value="KY">Kentucky</option>
                      <option value="LA">Louisiana</option>
                      <option value="ME">Maine</option>
                      <option value="MD">Maryland</option>
                      <option value="MA">Massachusetts</option>
                      <option value="MI">Michigan</option>
                      <option value="MN">Minnesota</option>
                      <option value="MS">Mississippi</option>
                      <option value="MO">Missouri</option>
                      <option value="MT">Montana</option>
                      <option value="NE">Nebraska</option>
                      <option value="NV">Nevada</option>
                      <option value="NH">New Hampshire</option>
                      <option value="NJ">New Jersey</option>
                      <option value="NM">New Mexico</option>
                      <option value="NY">New York</option>
                      <option value="NC">North Carolina</option>
                      <option value="ND">North Dakota</option>
                      <option value="OH">Ohio</option>
                      <option value="OK">Oklahoma</option>
                      <option value="OR">Oregon</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="RI">Rhode Island</option>
                      <option value="SC">South Carolina</option>
                      <option value="SD">South Dakota</option>
                      <option value="TN">Tennessee</option>
                      <option value="TX">Texas</option>
                      <option value="UT">Utah</option>
                      <option value="VT">Vermont</option>
                      <option value="VA">Virginia</option>
                      <option value="WA">Washington</option>
                      <option value="WV">West Virginia</option>
                      <option value="WI">Wisconsin</option>
                      <option value="WY">Wyoming</option>
                      <option value="DC">Washington DC</option>
                    </>
                  )}
                </select>
                {touched.state && errors.state && (
                  <p className="mt-1 text-sm text-red-400">{errors.state}</p>
                )}
              </div>

              <div>
                <label
                  className="block text-sm font-medium text-gray-300 mb-1">{formData.country === 'CA' ? 'Postal Code' : 'Zip Code'}</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={(e) => {
                    let value = e.target.value.toUpperCase()
                    if (formData.country === 'CA') {
                      // Canadian postal code format: A1A 1A1
                      value = value.replace(/[^A-Z0-9]/g, '')
                      if (value.length > 3) {
                        value = value.slice(0, 3) + ' ' + value.slice(3, 6)
                      }
                      value = value.slice(0, 7)
                    } else {
                      // US ZIP code format: 12345
                      value = value.replace(/\D/g, '').slice(0, 5)
                    }
                    handleChange({ target: { name: 'zipCode', value } })
                  }}
                  onBlur={handleBlur}
                  required
                  maxLength={formData.country === 'CA' ? '7' : '5'}
                  className="mt-1 block w-full rounded-xl bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:border-pink-500 focus:ring-pink-500 transition-all duration-200"
                  placeholder={formData.country === 'CA' ? 'A1A 1A1' : '12345'}
                />
                {touched.zipCode && errors.zipCode && (
                  <p className="mt-1 text-sm text-red-400">{errors.zipCode}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Current Relationship Status</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <label
                    className={`relative flex cursor-pointer rounded-xl p-3 border-2 transition-all duration-200 ${formData.relationshipStatus === 'single'
                      ? 'border-pink-500 bg-gray-700'
                      : 'border-gray-600 hover:border-pink-400'
                    }`}>
                    <input
                      type="radio"
                      name="relationshipStatus"
                      value="single"
                      checked={formData.relationshipStatus === 'single'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium text-gray-200">Single</span>
                  </label>
                  <label
                    className={`relative flex cursor-pointer rounded-xl p-3 border-2 transition-all duration-200 ${formData.relationshipStatus === 'not_single'
                      ? 'border-pink-500 bg-gray-700'
                      : 'border-gray-600 hover:border-pink-400'
                    }`}>
                    <input
                      type="radio"
                      name="relationshipStatus"
                      value="not_single"
                      checked={formData.relationshipStatus === 'not_single'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium text-gray-200">Not Single</span>
                  </label>
                </div>
                {errors.relationshipStatus && (
                  <p className="mt-1 text-sm text-red-400">{errors.relationshipStatus}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Gender</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <label
                    className={`relative flex cursor-pointer rounded-xl p-3 border-2 transition-all duration-200 ${formData.gender === 'female'
                      ? 'border-pink-500 bg-gray-700'
                      : 'border-gray-600 hover:border-pink-400'
                    }`}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium text-gray-200">Female</span>
                  </label>
                  <label
                    className={`relative flex cursor-pointer rounded-xl p-3 border-2 transition-all duration-200 ${formData.gender === 'male'
                      ? 'border-pink-500 bg-gray-700'
                      : 'border-gray-600 hover:border-pink-400'
                    }`}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium text-gray-200">Male</span>
                  </label>
                </div>
                {errors.gender && (
                  <p className="mt-1 text-sm text-red-400">{errors.gender}</p>
                )}
              </div>
            </div>

            {/* Registration submitted message */}
            {isRegistrationSubmitted && (
              <div className="mt-4 p-4 bg-green-900/50 border border-green-700 rounded-xl text-green-200">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span>Registration has been successfully submitted! You can now proceed to the next steps.</span>
                </div>
              </div>
            )}

            {/* Error message display */}
            {errors.submit && (
              <div className="mt-4 p-4 bg-red-900/50 border border-red-700 rounded-xl text-red-200">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span>{errors.submit}</span>
                </div>
              </div>
            )}

            {/* Consolidated Error Report */}
            {consolidatedErrors && (
              <div className="mt-6 p-6 bg-red-900/30 border border-red-600 rounded-xl">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 mr-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                  </svg>
                  <h3 className="text-lg font-semibold text-red-300">{consolidatedErrors.summary}</h3>
                </div>

                <div className="space-y-4">
                  {consolidatedErrors.details.map((detail, index) => (
                    <div key={index} className="bg-red-900/20 p-4 rounded-lg border border-red-700">
                      <div className="mb-3">
                        <h4 className="font-medium text-red-200 mb-1">{detail.field}</h4>
                        <div className="text-sm text-red-300 space-y-1">
                          <p><span className="font-medium">Original Value:</span> {detail.originalValue}</p>
                          <p><span className="font-medium">Error Type:</span> {detail.errorType}</p>
                          <p><span className="font-medium">Error Message:</span> {detail.errorMessage}</p>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium text-red-200 mb-2">Possible Solutions:</h5>
                        <ul className="text-sm text-red-300 space-y-1">
                          {detail.possibleSolutions.map((solution, solIndex) => (
                            <li key={solIndex} className="flex items-start">
                              <span className="text-red-400 mr-2">•</span>
                              <span>{solution}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>

                {consolidatedErrors.generalAdvice && consolidatedErrors.generalAdvice.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
                    <h5 className="font-medium text-blue-200 mb-2">General Advice:</h5>
                    <ul className="text-sm text-blue-300 space-y-1">
                      {consolidatedErrors.generalAdvice.map((advice, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          <span>{advice}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Square Customer Processing Error */}
            {squareCustomerError && (
              <div className="mt-4 p-4 bg-red-900/20 border border-red-700 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-200">
                      Square Customer Processing Error
                    </h3>
                    <div className="mt-2 text-sm text-red-300">
                      <p>{squareCustomerError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 sm:mt-6">
              <button
                type="submit"
                disabled={isProcessingSquareCustomer || isRegistrationSubmitted}
                className={`w-full rounded-xl px-4 py-3 font-semibold transition-all duration-200 ${(isProcessingSquareCustomer || isRegistrationSubmitted)
                  ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700'
                }`}
              >
                {isProcessingSquareCustomer
                  ? 'Processing with Square...'
                  : isRegistrationSubmitted
                    ? 'Registration Submitted Successfully'
                    : 'Submit Step 1'
                }
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx="true">{`
          @keyframes blob {
              0% {
                  transform: translate(0px, 0px) scale(1);
              }
              33% {
                  transform: translate(30px, -50px) scale(1.1);
              }
              66% {
                  transform: translate(-20px, 20px) scale(0.9);
              }
              100% {
                  transform: translate(0px, 0px) scale(1);
              }
          }

          .animate-blob {
              animation: blob 7s infinite;
          }

          .animation-delay-2000 {
              animation-delay: 2s;
          }

          .animation-delay-4000 {
              animation-delay: 4s;
          }

          @keyframes glow {
              0% {
                  box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
              }
              50% {
                  box-shadow: 0 0 20px rgba(147, 51, 234, 0.4), 0 0 30px rgba(147, 51, 234, 0.4);
              }
              100% {
                  box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
              }
          }

          .animate-glow {
              animation: glow 2s infinite;
          }

          @keyframes fade-in {
              from {
                  opacity: 0;
                  transform: translateY(-20px);
              }
              to {
                  opacity: 1;
                  transform: translateY(0);
              }
          }

          .animate-fade-in {
              animation: fade-in 1s ease-out;
          }
      `}</style>
    </div>
  )
}

export default Registration
